import Box from '@mui/material/Box'
import Slider from '@mui/material/Slider'
import Typography from '@mui/material/Typography'
import type { SurveyQuestion } from '../../../lib/survey-question.types'
import {
  formatSliderEndLabel,
  parseSliderBounds,
} from '../../../lib/slider-bounds'
import {
  surveySliderEndLabelEndSx,
  surveySliderEndLabelStartSx,
  surveySliderEndLabelsRowSx,
  surveySliderInvalidSx,
  surveySliderRootSx,
  surveySliderSx,
} from '../../../styles/surveyExecutionStyles'

interface SliderQuestionProps {
  readonly question: SurveyQuestion
  readonly value: number | null
  readonly onChange: (value: number) => void
}

export function SliderQuestion({ question, value, onChange }: SliderQuestionProps) {
  const bounds = parseSliderBounds(
    question.sliderMinValue,
    question.sliderMaxValue,
    question.sliderStep,
  )

  if (!bounds) {
    return (
      <Typography sx={surveySliderInvalidSx} data-testid={`survey-q-${question.id}-slider-invalid`}>
        This slider is not configured correctly.
      </Typography>
    )
  }

  const { min, max, step } = bounds
  const displayValue = value ?? min
  const leftEnd = formatSliderEndLabel(min, question.sliderLowLabel)
  const rightEnd = formatSliderEndLabel(max, question.sliderHighLabel)

  return (
    <Box sx={surveySliderRootSx} data-testid={`survey-q-${question.id}-slider`}>
      <Slider
        value={displayValue}
        min={min}
        max={max}
        step={step}
        valueLabelDisplay="on"
        aria-label={question.prompt}
        data-testid={`survey-q-${question.id}-slider-control`}
        onChange={(_, next) => onChange(next as number)}
        sx={surveySliderSx}
      />
      <Box sx={surveySliderEndLabelsRowSx} data-testid={`survey-q-${question.id}-slider-labels`}>
        <Typography component="span" sx={surveySliderEndLabelStartSx}>
          {leftEnd}
        </Typography>
        <Typography component="span" sx={surveySliderEndLabelEndSx}>
          {rightEnd}
        </Typography>
      </Box>
    </Box>
  )
}
